import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import ImageUploadComponent from 'components/ImageUploadComponent';
import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import { fetchBannerDetails, toggleBannerActiveStatus, updateBanner } from 'services';
import { showError, showSuccess } from 'utils';
import styles from './index.module.scss';

const BannerDetails = props => {
  const [banner, setBanner] = useState<Record<string,any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [activeLoading, setActiveLoading] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const { bannerId } = props.match.params;

  const fetchBannerDetailsFn = async () => {
    try {
      const response = await fetchBannerDetails(bannerId);
      setBanner(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBannerDetailsFn();
  }, []);
  
  const updateBannerFn = async () => {
    setUpdateLoading(true);
    const { active, ...rest } = banner;
    const data = {
      ...rest,
      id: bannerId,
    };
    try {
      await updateBanner(data);
      showSuccess('Details updated successfully');
      fetchBannerDetailsFn();
      setEditable(false);
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };
  
  const updateBannerImage = async (id: string, image: File) => {
    setUpdateLoading(true);
    const data = {
      ...banner,
      id,
      image,
    };
    try {
      await updateBanner(data);
      fetchBannerDetailsFn();
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };
  
  const toggleBannerActive = async (checked: boolean) => {
    setBanner({ ...banner, active: checked });
    setActiveLoading(true);
    try {
      await toggleBannerActiveStatus(bannerId);
      showSuccess(`Banner marked as ${checked ? 'ACTIVE' : 'INACTIVE'} successfully`);
      fetchBannerDetailsFn();
      setEditable(false);
    } catch (e) {
      showError(e);
    } finally {
      setActiveLoading(false);
    }
  };
  
  if (loading) return <Loading />;
  
  return (
    <div className={styles['main-container']}>
      <div className={styles['header']}>
        <div className={styles['heading']}>Banner Details</div>
        <BackButton />
      </div>
      <div className={styles['basic-details-container']}>
        <div className={styles['image-section']}>
          <div className={styles['image-container']}>
            <ImageUploadComponent
              src={banner.image._url}
              entityId={bannerId}
              toggleModal={() => {}}
              uploadAPI={updateBannerImage}
            />
          </div>
        </div>
        <div className={styles['details-section']}>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Banner ID</div>
            <div className={styles['detail']}>{banner.objectId}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Text</div>
            {editable ? (
              <input
                className={'form-control'}
                disabled={updateLoading}
                value={banner.text}
                onChange={e => setBanner({ ...banner, text: e.target.value })}
              />
            ): (
              <div className = {styles['detail']}>{banner.text}</div>
            )}
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>URL</div>
            {editable ? (
              <input
                className={'form-control'}
                disabled={updateLoading}
                value={banner.url}
                onChange={e => setBanner({ ...banner, url: e.target.value })}
              />
            ) : (
              <div className={styles['detail']}>{banner.url}</div>
            )}
          </div>
          <div className={styles['active-input-container']}>
            {activeLoading ? (
              <Loading type={'mini'} />
            ) : (
              <input
                type={'checkbox'}
                disabled={activeLoading}
                checked={banner.active}
                onChange={e => toggleBannerActive(e.target.checked)}
              />
            )}
            <div className={styles['active-label']}>Active</div>
          </div>
        </div>
        <div>
          {editable ? (
            <ActionButton
              text={'Save'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={updateBannerFn}
            />
          ): (
            <div
              onClick={() => setEditable(true)}
              className={styles['edit-button']}
            >
              Edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { BannerDetails };